import { Box, Divider, Typography } from "@mui/material";
import * as React from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppButton } from "ui/Scripts/source/components/framework/Button/AppButton";
import { ISharedPanel, ISharedPanelSave, SharedPanel, SharedPanelSave } from "source/models/panel/sharedPanel";
import { PageDrawer } from "source/components/framework/Page/PageDrawer";
import { AppChartIcon } from "source/components/framework/Icon/AppChartIcon";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { ModifyUserInfo } from "source/components/framework/User/ModifyUserInfo";
import { RemoveToolButton } from "source/components/framework/Button/RemoveToolButton";
import { UserGroupBlocks } from "source/components/userGroup/UserGroupBlocks";
import { IModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { AppUtils } from "source/framework/appUtils";
import { UserGroupListItems } from "ui/Scripts/source/models/userGroup/userGroupListItems";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Base } from "ui/Scripts/source/framework/base";

interface IManageSharedPanelsDrawerProps {
    companyId: string;
    sharedPanels: ISharedPanel[];
    onCancel: () => void;
    onSaveSharedPanel?: (companyId: string, orgSharedPanelId: string, sharedPanel: ISharedPanelSave) => Promise<ApiSuccess>;
    onGetUserGroupSelectListItems: (companyId: string, pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, groupType: number,
        ignoreUserGroupCodes: string[], requireUserGroupCodes: string[], ignoreQrCodeUserGroups: boolean, requireQrCodeUserGroups: boolean, messageTargetMode: boolean) => Promise<UserGroupListItems>;
}

interface IManageSharedPanelsDrawerState {
    sharedPanels: ISharedPanel[];
    selectedId: string;
}

export const ManageSharedPanelsDrawer = (props: IManageSharedPanelsDrawerProps) => {
    const [state, setState] = React.useState<IManageSharedPanelsDrawerState>({
        sharedPanels: props.sharedPanels.slice(0).sort((a, b) => Base.strCompare(a.id, b.id)),
        selectedId: ""
    });

    const handleRemove = async (panel: ISharedPanel) => {
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveSharedPanelParameter, panel.id), false) === ConfirmationDialogResult.Yes) {
            const result = await AppUtils.callService(() => props.onSaveSharedPanel(props.companyId, panel.id, null), false);
            if (!result) return false;
            setState((old) => ({
                ...old,
                sharedPanels: old.sharedPanels.filter(i => i.id !== panel.id)
            }));
            return true;
        }
        return false;
    };

    // #region UserGroups
    const handleAddUserGroups = async (panel: ISharedPanel, userGroups: IModifyUser[]): Promise<ApiSuccess> => {
        if (!userGroups || userGroups.length < 1) return null;
        const newSharedPanel = new SharedPanel(panel);
        newSharedPanel.userGroups = newSharedPanel.userGroups.concat(userGroups);
        const saveSharedPanel = new SharedPanelSave(newSharedPanel);
        const result = await AppUtils.callService(() => props.onSaveSharedPanel(props.companyId, saveSharedPanel.id, saveSharedPanel), false);
        if (!result) return null;
        setState((old) => ({
            ...old,
            sharedPanels: old.sharedPanels.map(i => i.id === panel.id ? newSharedPanel : i)
        }));
        return new ApiSuccess();
    };

    const handleRemoveUserGroups = async (panel: ISharedPanel, userGroupCodes: string[]): Promise<ApiSuccess> => {
        if (!userGroupCodes || userGroupCodes.length < 1) return null;
        const newSharedPanel = new SharedPanel(panel);
        newSharedPanel.userGroups = newSharedPanel.userGroups.filter(i => userGroupCodes.indexOf(i.code) < 0);
        if (newSharedPanel.userGroups.length < 1) {
            const removeResult = await handleRemove(panel);
            if (removeResult) return new ApiSuccess();
        };
        const saveSharedPanel = new SharedPanelSave(newSharedPanel);
        const result = await AppUtils.callService(() => props.onSaveSharedPanel(props.companyId, saveSharedPanel.id, saveSharedPanel), false);
        if (!result) return null;
        setState((old) => ({
            ...old,
            sharedPanels: old.sharedPanels.map(i => i.id === panel.id ? newSharedPanel : i)
        }));
        return new ApiSuccess();
    };
    // #endregion UserGroups
    
    return (
        <PageDrawer
            open={true}
            icon={<AppChartIcon />}
            title={Translations.ManageSharedPanels}
            parentTitle={Translations.Panels}
            onClose={props.onCancel}
            fitToContainer
        >
            <VerticalContainer
                minHeight="100%"
                after={
                    <>
                        <Divider sx={{ margin: "1rem 0" }} />
                        <Box display="flex" justifyContent="flex-end" margin="1rem">
                            <AppButton variant="outlined" onClick={props.onCancel} sx={{ marginRight: ".5rem" }}>{Translations.Close}</AppButton>
                        </Box>
                    </>
                }
            >
                <Box display="flex" flexDirection="column" margin="24px" height="100%" overflow="auto">
                    {state.sharedPanels.map(panel => {
                        const selected = panel.id === state.selectedId;
                        return <Box
                            key={panel.id}
                            onClick={() => setState(old => ({ ...old, selectedId: panel.id }))}
                            sx={{
                                borderRadius: ".5rem",
                                border: "1px solid",
                                borderColor: "divider",
                                backgroundColor: selected ? "action.selected" : "action.hover",
                                marginBottom: "1rem",
                                padding: "6px 0",
                                cursor: "pointer",
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1, justifyContent: "space-between" }}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Box margin="0 8px" sx={{ color: panel.visible ? "text.secondary" : "text.disabled" }}>{panel.getIcon({ color: panel.userGroups && panel.userGroups.length > 0 ? undefined : "warning" })}</Box>
                                    <Typography sx={{ color: panel.visible ? "text.secondary" : "text.disabled" }}>{panel.id}</Typography>
                                </Box>
                                <RemoveToolButton
                                    onClick={() => handleRemove(panel)}
                                />
                            </Box>
                            {selected &&
                                <Box padding="8px">
                                    <Box sx={{ bgcolor: "background.paper", borderRadius: ".5rem", padding: "8px" }}>
                                        <Box>
                                            <FormField title={Translations.SharedBy} fullWidth margin={0}>
                                                <ModifyUserInfo
                                                    modifyUser={panel.shareUser}
                                                />
                                            </FormField>
                                        </Box>
                                        <Box onMouseEnter={(e) => e.stopPropagation()}>
                                            <FormField title={Translations.UserGroups} fullWidth>
                                                <UserGroupBlocks
                                                    companyId={props.companyId}
                                                    margin={0}
                                                    listModeChangeDisabled={true}
                                                    title={null}
                                                    selectionTitle={Translations.SelectUserGroups}
                                                    userGroups={panel.userGroups}
                                                    removeUsersConfirmation={Translations.DoYouReallyWantToRemoveSelectedUserGroups}
                                                    onAddUserGroups={(userGroups: IModifyUser[]) => handleAddUserGroups(panel, userGroups)}
                                                    onRemoveUserGroups={(userGroupCodes: string[]) => handleRemoveUserGroups(panel, userGroupCodes)}
                                                    onGetUserGroupSelectListItems={props.onGetUserGroupSelectListItems}
                                                />
                                            </FormField>
                                        </Box>
                                    </Box>
                                </Box>
                            }
                        </Box>;
                    })}
                </Box>
            </VerticalContainer>
        </PageDrawer>
    );
};
